import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import classes from "./InlineEditText.scss";
import classNames from "classnames";
import { IconButton, Tooltip } from "@toddle-edu/ds-web";
import { PencilLineOutlined } from "@toddle-edu/ds-icons";
import { useTranslation } from "react-i18next";
import { useConditionalTooltip } from "TranscriptsV3/hooks";

const InlineEditText = memo(
  ({ children, value, className, testId, placeholder, onSave }) => {
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [input, setInput] = useState(value);

    useEffect(() => {
      setInput(value);
    }, [value]);

    const updatedPlaceholder = useMemo(() => {
      return placeholder || t("common:enter_text");
    }, [placeholder, t]);

    const toggleEdit = useCallback(() => {
      setIsEditing(prev => !prev);
    }, []);

    const handleBlur = useCallback(
      e => {
        const trimmedValue = e.target.value.trim();
        const updatedValue = trimmedValue === "" ? value : trimmedValue;

        if (updatedValue === value) setInput(value);
        else if (onSave) onSave(updatedValue);

        toggleEdit();
      },
      [onSave, toggleEdit, value]
    );

    const handleChange = useCallback(e => {
      setInput(e.target.value);
    }, []);

    const handleKeyDown = useCallback(
      e => {
        e.stopPropagation();
        const key = e.key;

        switch (key) {
          case "Escape":
            setInput(value);
            toggleEdit();
            break;
          case "Enter":
            handleBlur(e);
            break;
          default:
            break;
        }
      },
      [handleBlur, toggleEdit, value]
    );

    const inputClasses = classNames(classes.input, className);

    if (isEditing)
      return (
        <input
          data-test-id={`${testId}-inlineEdit-input`}
          autoFocus
          className={inputClasses}
          value={input}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onClick={e => e.stopPropagation()}
          type="text"
          placeholder={updatedPlaceholder}
          aria-label={updatedPlaceholder}
          dir="auto"
        />
      );

    return (
      <div className={classes.container}>
        {React.Children.map(children, child =>
          React.isValidElement(child)
            ? React.cloneElement(child, {
                onClick: toggleEdit,
                label: input,
                testId,
                t,
              })
            : child
        )}
      </div>
    );
  }
);

InlineEditText.displayName = "InlineEditText";

InlineEditText.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.string.isRequired,
  className: PropTypes.string,
  testId: PropTypes.string,
  placeholder: PropTypes.string,
  onSave: PropTypes.func,
};

InlineEditText.defaultProps = {
  className: "text-body",
};

const Text = memo(({ label, className, t, id, tag: Tag }) => {
  const textWrapperRef = useRef(null);
  const textClasses = classNames(className, classes.text);

  const isTooltipVisible = useConditionalTooltip({ textWrapperRef });

  return (
    <Tooltip tooltip={label} visible={isTooltipVisible}>
      <Tag ref={textWrapperRef} className={textClasses} id={id} dir="auto">
        {label || t("common:untitled")}
      </Tag>
    </Tooltip>
  );
});
Text.displayName = "InlineEditText.Text";

Text.propTypes = {
  label: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
  tag: PropTypes.string,
};

Text.defaultProps = {
  className: "text-body",
  tag: "div",
};

const EditButton = memo(({ onClick, testId, buttonProps, iconProps, t }) => {
  return (
    <IconButton
      onClick={onClick}
      icon={<PencilLineOutlined variant="subtle" {...iconProps} />}
      testId={`${testId}-inlineEdit-editButton`}
      isCompact
      type="plain"
      variant="neutral"
      shouldStopPropagation
      aria-label={t("common:edit")}
      {...buttonProps}
    />
  );
});
EditButton.displayName = "InlineEditText.EditButton";

EditButton.propTypes = {
  onClick: PropTypes.func,
  testId: PropTypes.string,
  buttonProps: PropTypes.object,
  iconProps: PropTypes.object,
};

EditButton.defaultProps = {
  buttonProps: {},
  iconProps: {},
};

InlineEditText.EditButton = EditButton;
InlineEditText.Text = Text;

export default InlineEditText;
